import type { ReactElement } from 'react'
import { confirmDelete, useRequiredParams } from '@util'
import { type CustomFieldData, type CustomFieldId, type UnsavedCustomField } from '@terros/common'
import { useRouter, createFileRoute } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { useToastError } from '@hooks'
import { useCustomField, useRemoveCustomField, useUpdateCustomField, useTranslation } from '@front/model'
import {
  ButtonDelete,
  ButtonSave,
  DangerZone,
  NotReady,
  Page,
  TAuditInfoSection,
  TAlert,
  TButton,
  TListItem,
} from '@components'
import { useCustomFieldForm } from '../settings.customField_.add/util'
import { CustomFieldForm } from '../settings.customField_.add/Form'

function CustomField(): ReactElement {
  const { fieldId } = useRequiredParams<{ fieldId: CustomFieldId }>()
  const { data, isLoading, error } = useCustomField(fieldId)

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Custom Field' isLoading={isLoading} />

  return <EditCustomFieldForm data={data} />
}

const EditCustomFieldForm = ({ data }: { data: CustomFieldData }): ReactElement => {
  const { update, isUpdating, error: updateError } = useUpdateCustomField()
  const { remove, isRemoving, error: removeError } = useRemoveCustomField()
  const { t } = useTranslation()
  useToastError(updateError)
  useToastError(removeError)

  const router = useRouter()

  const handleRestore = async (): Promise<void> => {
    await update({ ...data, isDeleted: false })
    notifications.show({ message: t('Custom field restored'), color: 'green' })
  }

  const handleMoveToTrash = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Move Custom Field to Trash'),
      message: t('You can restore this custom field anytime from the Trash.'),
      label: t('Move to Trash'),
      t,
    })
    if (!confirmed) return

    await remove({ fieldId: data.fieldId })
    notifications.show({ message: t('Custom Field Moved to Trash'), color: 'green' })
    router.history.back()
  }

  const handleSubmit = async (unsaved: UnsavedCustomField): Promise<void> => {
    await update({ ...unsaved, fieldId: data.fieldId })
    router.history.back()
    form.resetDirty()
    notifications.show({ message: t('Custom field saved'), color: 'green' })
  }

  const form = useCustomFieldForm(data)

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back title={t('Edit Custom Field')}>
          <ButtonSave disabled={!form.isDirty()} loading={isUpdating} type='submit' />
        </Page.Heading>
        {data.isDeleted && (
          <Page.FunctionBar>
            <TAlert className='w-full' variant='discovery' title={t('This custom field is in Trash')}>
              <TButton loading={isUpdating} variant='secondary' onClick={handleRestore}>
                {t('Restore')}
              </TButton>
            </TAlert>
          </Page.FunctionBar>
        )}
        <Page.Content scrollArea>
          <CustomFieldForm edit form={form} />
          <TAuditInfoSection {...data}>
            <TListItem canCopy detailBold details={data.fieldId} label={t('Custom Field ID')} />
          </TAuditInfoSection>
          {!data.isDeleted && (
            <DangerZone>
              <ButtonDelete loading={isRemoving} onClick={handleMoveToTrash}>
                {t('Move to Trash')}
              </ButtonDelete>
            </DangerZone>
          )}
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/customField_/$fieldId/')({
  component: CustomField,
})
